import { useEffect, type CSSProperties } from "react";
import { useAtomValue } from "jotai";
import { createFileRoute } from "@tanstack/react-router";
import {
  CalendarDndProvider,
  TodayView,
  EventGap,
  EventHeight,
  MonthView,
  WeekCellsHeight,
  WeekView,
} from "@/components/calendar/event-calendar";
import { ListView } from "@/components/calendar/event-calendar";
import { useUser } from "@/hooks/useUser";
import { cn } from "@/lib/utils";
import { useRoutines } from "@/hooks/routines/useRoutines";
import { useGoals } from "@/hooks/goals/useGoals";
import { calendarCurrentDateAtom } from "@/state/dashboardPreferences";
import { useMobileNavbar } from "@/providers/mobileNavbarProvider";
import { useMobile } from "@/providers/mobileProvider";
import { useCalendarView } from "@/providers/preferencesProvider";

export const Route = createFileRoute("/dashboard/calendar")({
  component: CalendarPage,
});

function CalendarPage() {
  const { user } = useUser();
  const { routines, handleCreateRoutine } = useRoutines();
  const { goals, handleCreateGoal } = useGoals();
  const currentDate = useAtomValue(calendarCurrentDateAtom);
  const { isMobile } = useMobile();
  const { setMobileNavbar } = useMobileNavbar();
  const { calendarView } = useCalendarView();

  // Set up navbar configuration
  useEffect(() => {
    if (isMobile) {
      setMobileNavbar({
        title: undefined,
        showBackButton: false,
      });
    }
  }, [isMobile, setMobileNavbar]);

  return (
    <div className="col-span-12 select-none">
      <div
        className={cn(
          "flex has-data-[slot=month-view]:flex-1 flex-col bg-background mb-[44px] md:mb-0",
          {
            "min-h-screen": calendarView === "month",
          },
        )}
        style={
          {
            "--event-height": `${EventHeight}px`,
            "--event-gap": `${EventGap}px`,
            "--week-cells-height": `${WeekCellsHeight}px`,
          } as CSSProperties
        }
      >
        <CalendarDndProvider>
          <div className="flex flex-1 flex-col">
            {calendarView === "month" && (
              <MonthView
                routines={routines}
                goals={goals}
                currentDate={currentDate}
                weekStartsOn={(user?.weekStartsOn as 0 | 1) ?? 1}
                handleCreateGoal={handleCreateGoal}
                handleCreateRoutine={handleCreateRoutine}
              />
            )}
            {calendarView === "week" && (
              <WeekView
                routines={routines}
                goals={goals}
                currentDate={currentDate}
                timeFormat={user?.timeFormat}
                weekStartsOn={(user?.weekStartsOn as 0 | 1) ?? 1}
                handleCreateGoal={handleCreateGoal}
                handleCreateRoutine={handleCreateRoutine}
              />
            )}
            {calendarView === "day" && (
              <TodayView
                routines={routines}
                goals={goals}
                currentDate={currentDate}
                timeFormat={user?.timeFormat}
                handleCreateGoal={handleCreateGoal}
                handleCreateRoutine={handleCreateRoutine}
              />
            )}
            {calendarView === "list" && (
              <ListView currentDate={currentDate} routines={routines} goals={goals} />
            )}
          </div>
        </CalendarDndProvider>
      </div>
    </div>
  );
}
